import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import '../styles/Chatbot.css';
import { useAppState } from '../context/AppStateContext';

const Chatbot = () => {
    const { chatbotVisible } = useAppState();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Hey! I am ArenaX, your smart support assistant. How can I help you today? 🎮'
        }
    ]);

    // if (!chatbotVisible) return null; // Removed early return to prevent hook count mismatch
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [windowPosition, setWindowPosition] = useState({ x: 0, y: 0 }); // Track window movement

    // Initialize button position from localStorage or default to {0, 0}
    const [buttonPosition, setButtonPosition] = useState(() => {
        const savedPos = localStorage.getItem('chatbot_btn_pos');
        return savedPos ? JSON.parse(savedPos) : { x: 0, y: 0 };
    });

    const [isDraggingWindow, setIsDraggingWindow] = useState(false);
    const [isDraggingButton, setIsDraggingButton] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false); // To distinguish click from drag

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Handle Keyboard Shortcuts (Ctrl + I)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.key === 'i') {
                e.preventDefault();
                setIsOpen(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 300); // Wait for open animation
        }
    }, [isOpen]);

    // DRAGGABLE LOGIC FOR WINDOW
    const handleWindowMouseDown = (e) => {
        if (e.target.closest('.chatbot-close')) return;
        setIsDraggingWindow(true);
        setDragStart({
            x: e.clientX - windowPosition.x,
            y: e.clientY - windowPosition.y
        });
    };

    // DRAGGABLE LOGIC FOR BUTTON
    const handleButtonMouseDown = (e) => {
        setIsDraggingButton(true);
        setHasMoved(false);
        setDragStart({
            x: e.clientX - buttonPosition.x,
            y: e.clientY - buttonPosition.y
        });
    };

    // TOUCH HELPERS FOR MOBILE
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        if (e.target.closest('.chatbot-close')) return;

        // Handle Button drag
        if (e.target.closest('.chatbot-toggle')) {
            setIsDraggingButton(true);
            setHasMoved(false);
            setDragStart({
                x: touch.clientX - buttonPosition.x,
                y: touch.clientY - buttonPosition.y
            });
        }
        // Handle Window drag
        else if (e.target.closest('.chatbot-header')) {
            setIsDraggingWindow(true);
            setDragStart({
                x: touch.clientX - windowPosition.x,
                y: touch.clientY - windowPosition.y
            });
        }
    };

    const handleMouseMove = (e) => {
        const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

        if (isDraggingWindow) {
            let newX = clientX - dragStart.x;
            let newY = clientY - dragStart.y;

            // Screen Boundaries for Window
            const padding = 20;
            const containerWidth = 380;
            const containerHeight = 550;

            // Calculate limits based on button position (since window is relative to it)
            const minX = -window.innerWidth + containerWidth + padding;
            const maxX = padding;
            const minY = -window.innerHeight + containerHeight + padding;
            const maxY = padding;

            // Constrain
            // Note: Since the container has right: 2rem and bottom: 5.5rem, we need to be careful
            // For simplicity, let's use the actual screen dimensions
            const winWidth = 380;
            const winHeight = 550;

            // Get the toggle button's fixed position (right: 2rem, bottom: 2rem)
            // We'll just ensure the final absolute position is within [0, innerWidth]

            setWindowPosition({ x: newX, y: newY });
        } else if (isDraggingButton) {
            setHasMoved(true);
            let newX = clientX - dragStart.x;
            let newY = clientY - dragStart.y;

            // Screen Boundaries for Toggle Button (60x60px)
            // Initial position is bottom: 2rem (32px), right: 2rem (32px)
            const btnSize = 60;
            const margin = 10;

            // Max allowed movement left (negative X)
            const limitLeft = -(window.innerWidth - btnSize - margin - 32);
            // Max allowed movement right (positive X)
            const limitRight = 32;
            // Max allowed movement up (negative Y)
            const limitUp = -(window.innerHeight - btnSize - margin - 32);
            // Max allowed movement down (positive Y)
            const limitDown = 32;

            newX = Math.max(limitLeft, Math.min(newX, limitRight));
            newY = Math.max(limitUp, Math.min(newY, limitDown));

            setButtonPosition({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        if (isDraggingButton) {
            localStorage.setItem('chatbot_btn_pos', JSON.stringify(buttonPosition));
        }
        setIsDraggingWindow(false);
        setIsDraggingButton(false);
    };

    useEffect(() => {
        if (isDraggingWindow || isDraggingButton) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleMouseMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDraggingWindow, isDraggingButton, windowPosition, buttonPosition]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = inputMessage.trim();
        setInputMessage('');

        // Add user message
        const newMessages = [...messages, { role: 'user', content: userMessage }];
        setMessages(newMessages);
        setIsLoading(true);

        try {
            const history = messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }));

            const response = await axios.post(
                `${backendUrl}/api/chatbot/message`,
                {
                    message: userMessage,
                    history: history
                },
                {
                    withCredentials: true
                }
            );

            if (response.data.success) {
                setMessages([...newMessages, {
                    role: 'assistant',
                    content: response.data.message
                }]);
            }
        } catch (error) {
            console.error('Chatbot error:', error);
            setMessages([...newMessages, {
                role: 'assistant',
                content: 'Sorry, something went wrong. Please try again later. 😔'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleChatbot = () => {
        if (!hasMoved) {
            const newOpenState = !isOpen;
            setIsOpen(newOpenState);

            // If opening the chat, set button to specific position
            if (newOpenState) {
                const targetPos = { x: -0.92681884765625, y: 17.0731201171875 };
                setButtonPosition(targetPos);
                localStorage.setItem('chatbot_btn_pos', JSON.stringify(targetPos));
            }
        }
    };

    if (!chatbotVisible) return null;

    return (
        <>
            {/* Floating Chat Button (Draggable) */}
            <button
                className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
                onMouseDown={handleButtonMouseDown}
                onTouchStart={handleTouchStart}
                onClick={toggleChatbot}
                style={{
                    transform: `translate(${buttonPosition.x}px, ${buttonPosition.y}px) ${isOpen ? '' : 'scale(1)'}`,
                    transition: isDraggingButton ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: isDraggingButton ? 'grabbing' : 'pointer'
                }}
                aria-label="Toggle chatbot"
            >
                {isOpen ? (
                    <i className="ri-close-line"></i>
                ) : (
                    <i className="ri-customer-service-2-fill"></i>
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div
                    className="chatbot-container"
                    style={{
                        transform: `translate(${windowPosition.x + buttonPosition.x}px, ${windowPosition.y + buttonPosition.y}px)`,
                        transition: isDraggingWindow ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    <div className="chatbot-header" onMouseDown={handleWindowMouseDown}
                        onTouchStart={handleTouchStart}>
                        <div className="chatbot-header-content">
                            <i className="ri-customer-service-2-fill"></i>
                            <div>
                                <h3>ArenaX Support</h3>
                                <span className="chatbot-status">
                                    <span className="status-dot"></span>
                                    Online
                                </span>
                            </div>
                        </div>
                        <button
                            className="chatbot-close"
                            onClick={toggleChatbot}
                            aria-label="Close chatbot"
                        >
                            <i className="ri-close-line"></i>
                        </button>
                    </div>

                    <div className="chatbot-messages">
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`message ${msg.role === 'user' ? 'user-message' : 'bot-message'}`}
                            >
                                <div className="message-avatar">
                                    {msg.role === 'user' ? (
                                        <i className="ri-user-fill"></i>
                                    ) : (
                                        <i className="ri-robot-fill"></i>
                                    )}
                                </div>
                                <div className="message-content">
                                    <p>{msg.content}</p>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="message bot-message">
                                <div className="message-avatar">
                                    <i className="ri-robot-fill"></i>
                                </div>
                                <div className="message-content">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="chatbot-input-container" onSubmit={handleSendMessage}>
                        <input
                            type="text"
                            ref={inputRef}
                            className="chatbot-input"
                            placeholder="Type your question here..."
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            className="chatbot-send-btn"
                            disabled={!inputMessage.trim() || isLoading}
                            aria-label="Send message"
                        >
                            <i className="ri-send-plane-fill"></i>
                        </button>
                    </form>

                    <div className="chatbot-footer">
                        <p>ArenaX AI Support</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default Chatbot;
